export function todayLocalDate(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const value = Object.fromEntries(
    parts.map((part) => [part.type, part.value]),
  );
  return `${value.year}-${value.month}-${value.day}`;
}

export function addDays(date: string, days: number) {
  const value = new Date(`${date}T12:00:00`);
  value.setDate(value.getDate() + days);
  return value.toISOString().slice(0, 10);
}

export function addWorkingDays(date: string, days: number) {
  if (!days) return date;
  const value = new Date(`${date}T12:00:00`);
  const direction = days > 0 ? 1 : -1;
  let remaining = Math.abs(days);
  while (remaining > 0) {
    value.setDate(value.getDate() + direction);
    const weekday = value.getDay();
    if (weekday !== 0 && weekday !== 6) remaining -= 1;
  }
  return value.toISOString().slice(0, 10);
}

export function isProjectWorkingDay(
  date: string,
  workingWeekdays: readonly number[] = [1, 2, 3, 4, 5],
  holidays: readonly string[] = [],
) {
  const jsWeekday = new Date(`${date}T12:00:00`).getDay();
  const isoWeekday = jsWeekday === 0 ? 7 : jsWeekday;
  return workingWeekdays.includes(isoWeekday) && !holidays.includes(date);
}

export function addProjectWorkingDays(
  date: string,
  days: number,
  workingWeekdays: readonly number[] = [1, 2, 3, 4, 5],
  holidays: readonly string[] = [],
) {
  if (!Number.isInteger(days))
    throw new RangeError("Working-day offset must be a whole number.");
  if (!workingWeekdays.some((weekday) => weekday >= 1 && weekday <= 7))
    throw new RangeError("The project calendar needs a working day.");
  let cursor = date;
  const direction = days < 0 ? -1 : 1;
  while (!isProjectWorkingDay(cursor, workingWeekdays, holidays))
    cursor = addDays(cursor, direction);
  let remaining = Math.abs(days);
  while (remaining > 0) {
    cursor = addDays(cursor, direction);
    if (isProjectWorkingDay(cursor, workingWeekdays, holidays)) remaining -= 1;
  }
  return cursor;
}

export function calculateProjectFinish(
  startDate: string,
  durationWorkingDays: number,
  workingWeekdays: readonly number[] = [1, 2, 3, 4, 5],
  holidays: readonly string[] = [],
) {
  if (!Number.isInteger(durationWorkingDays) || durationWorkingDays < 1)
    throw new RangeError("Duration must be at least one whole working day.");
  return addProjectWorkingDays(
    startDate,
    durationWorkingDays - 1,
    workingWeekdays,
    holidays,
  );
}

export function workingDaysBetween(start: string, end: string) {
  if (start === end) return 0;
  const direction = end > start ? 1 : -1;
  let cursor = start;
  let result = 0;
  while (cursor !== end) {
    cursor = addDays(cursor, direction);
    const weekday = new Date(`${cursor}T12:00:00`).getDay();
    if (weekday !== 0 && weekday !== 6) result += direction;
  }
  return result;
}

export function shortDate(date: string) {
  if (!date) return "Not scheduled";
  return new Intl.DateTimeFormat("en-CA", {
    month: "short",
    day: "numeric",
  }).format(new Date(`${date}T12:00:00`));
}

export function fullDate(date: string) {
  if (!date) return "Not scheduled";
  return new Intl.DateTimeFormat("en-CA", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date(`${date}T12:00:00`));
}

export function daysBetween(start: string, end: string) {
  return Math.round(
    (new Date(`${end}T12:00:00`).getTime() -
      new Date(`${start}T12:00:00`).getTime()) /
      86400000,
  );
}
